
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import SEO from '../components/SEO';

const MentorshipApplication = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    institution: '',
    currentStatus: '',
    areaOfInterest: '',
    motivation: '',
    expectations: ''
  });

  const [applicationLetter, setApplicationLetter] = useState<File | null>(null);
  const [cv, setCv] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const uploadFile = async (file: File, prefix: string): Promise<string | null> => {
    const fileName = `${prefix}/${Date.now()}_${file.name}`;
    const { error } = await supabase.storage
      .from('application-documents')
      .upload(fileName, file);
    if (error) throw error;
    const { data } = supabase.storage
      .from('application-documents')
      .getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      let letterUrl: string | null = null;
      let cvUrl: string | null = null;

      if (applicationLetter) {
        letterUrl = await uploadFile(applicationLetter, 'letters');
      }
      if (cv) {
        cvUrl = await uploadFile(cv, 'cvs');
      }

      const { error } = await supabase.from('mentorship_applications').insert({
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        institution: formData.institution || null,
        current_status: formData.currentStatus,
        area_of_interest: formData.areaOfInterest,
        motivation: formData.motivation,
        expectations: formData.expectations || null,
        application_letter_url: letterUrl,
        cv_url: cvUrl,
      });

      if (error) throw error;
      setSubmitted(true);
    } catch (err: any) {
      toast({ title: 'Error', description: 'Failed to submit application. Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white py-16">
        <SEO
          title={`Mentorship Application — Law and Code Africa`}
          description={`Apply to the Law and Code Africa mentorship programme. Submit your application letter and CV to join.`}
          path="/mentorship-application"
        />
        <div className="max-w-2xl mx-auto px-4 md:px-8 text-center">
          <div className="bg-green-50 border border-green-200 rounded-lg p-8">
            <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-green-800 mb-4">Application Submitted Successfully!</h2>
            <p className="text-green-700 mb-6">
              Thank you for applying to the Law and Code Africa Mentorship Programme. 
              We will review your application and get back to you soon.
            </p>
            <Link to="/mentorship" className="inline-block bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300">
              Back to Mentorship
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <section className="relative py-16 bg-gradient-to-r from-primary to-secondary">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Mentorship Application</h1>
          <p className="text-xl text-blue-100">Join the Law and Code Africa Mentorship Programme</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 md:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 border">
            <h2 className="text-2xl font-bold text-primary mb-6">Application Form</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input type="text" id="fullName" name="fullName" required value={formData.fullName} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Enter your full name" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="your@email.com" />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="+254..." />
                </div>
              </div>

              <div>
                <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-2">Institution/Organization</label>
                <input type="text" id="institution" name="institution" value={formData.institution} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="University, law firm, or organization" />
              </div>

              <div>
                <label htmlFor="currentStatus" className="block text-sm font-medium text-gray-700 mb-2">Current Status *</label>
                <select id="currentStatus" name="currentStatus" required value={formData.currentStatus} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                  <option value="">Select your current status</option>
                  <option value="law-student">Law Student</option>
                  <option value="pupil-advocate">Pupil/Trainee Advocate</option>
                  <option value="practicing-lawyer">Practicing Lawyer</option>
                  <option value="legal-tech">Legal Tech Professional</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="areaOfInterest" className="block text-sm font-medium text-gray-700 mb-2">Area of Interest *</label>
                <select id="areaOfInterest" name="areaOfInterest" required value={formData.areaOfInterest} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                  <option value="">Select your area of interest</option>
                  <option value="ai-ethics">AI Ethics & Compliance</option>
                  <option value="legal-tech">Legal Technology</option>
                  <option value="digital-dispute">Digital Dispute Resolution</option>
                  <option value="data-privacy">Data Privacy Law</option>
                  <option value="cyber-law">Cyber Law</option>
                  <option value="ip-law">Intellectual Property Law</option>
                </select>
              </div>

              <div>
                <label htmlFor="motivation" className="block text-sm font-medium text-gray-700 mb-2">Why do you want to join this programme? *</label>
                <textarea id="motivation" name="motivation" required rows={4} value={formData.motivation} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Tell us about your motivation..." />
              </div>

              <div>
                <label htmlFor="expectations" className="block text-sm font-medium text-gray-700 mb-2">What do you expect to gain from this mentorship?</label>
                <textarea id="expectations" name="expectations" rows={3} value={formData.expectations} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Share your expectations..." />
              </div>

              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-primary mb-4">Required Documents</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="applicationLetter" className="block text-sm font-medium text-gray-700 mb-2">Application Letter</label>
                    <input type="file" id="applicationLetter" required accept=".pdf,.doc,.docx"
                      onChange={(e) => setApplicationLetter(e.target.files?.[0] || null)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20" />
                    <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, DOC, DOCX</p>
                    {applicationLetter && <p className="text-sm text-green-600 mt-1">✓ {applicationLetter.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="cv" className="block text-sm font-medium text-gray-700 mb-2">Curriculum Vitae (CV)</label>
                    <input type="file" id="cv" required accept=".pdf,.doc,.docx"
                      onChange={(e) => setCv(e.target.files?.[0] || null)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20" />
                    <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, DOC, DOCX</p>
                    {cv && <p className="text-sm text-green-600 mt-1">✓ {cv.name}</p>}
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <button type="submit" disabled={loading}
                  className="w-full bg-secondary hover:bg-secondary/90 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:shadow-lg disabled:opacity-50">
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>

              <p className="text-sm text-gray-500 text-center">
                By submitting this application, you agree to be contacted regarding the mentorship programme.
              </p>
            </form>
          </div>

          <div className="mt-8 text-center">
            <Link to="/mentorship" className="text-primary hover:text-secondary font-medium transition-colors">
              ← Back to Mentorship Programme
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MentorshipApplication;
